import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/i18n/LanguageContext";
import { Volume2, Mic, Monitor, Layers, Play, Square } from "lucide-react";
import { AudioVisualizer } from "./AudioVisualizer";

export function AudioInputCard({
  audioSourceType,
  setAudioSourceType,
  isRecording,
  startRecording,
  stopRecording,
  currentAudioLevel,
  gateThreshold,
  isGateOpen
}) {
  const { t } = useLanguage();

  return (
    <Card className="col-span-12 md:col-span-7 bento-card" data-testid="audio-input-card">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
          <Volume2 className="w-5 h-5" />
          {t("audioInput")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <AudioVisualizer 
          currentAudioLevel={currentAudioLevel}
          gateThreshold={gateThreshold}
          isGateOpen={isGateOpen}
        />
        
        {/* Audio Source Selection */}
        <div className="space-y-2">
          <Label className="text-zinc-400 text-sm">{t("audioSource")}</Label>
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant={audioSourceType === "microphone" ? "default" : "outline"}
              size="sm"
              onClick={() => setAudioSourceType("microphone")}
              disabled={isRecording}
              className={audioSourceType === "microphone" 
                ? "bg-white text-black hover:bg-zinc-200" 
                : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}
            >
              <Mic className="w-4 h-4 mr-1" />
              {t("microphone")}
            </Button>
            <Button
              variant={audioSourceType === "system" ? "default" : "outline"}
              size="sm"
              onClick={() => setAudioSourceType("system")}
              disabled={isRecording}
              className={audioSourceType === "system" 
                ? "bg-white text-black hover:bg-zinc-200" 
                : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}
            >
              <Monitor className="w-4 h-4 mr-1" />
              {t("system")}
            </Button>
            <Button
              variant={audioSourceType === "both" ? "default" : "outline"}
              size="sm"
              onClick={() => setAudioSourceType("both")}
              disabled={isRecording}
              className={audioSourceType === "both" 
                ? "bg-white text-black hover:bg-zinc-200" 
                : "border-zinc-700 text-zinc-300 hover:bg-zinc-800"}
            >
              <Layers className="w-4 h-4 mr-1" />
              {t("both")}
            </Button>
          </div>
          {audioSourceType !== "microphone" && (
            <p className="text-xs text-zinc-500">
              {audioSourceType === "system" 
                ? "Ideal for online meetings (Teams, Zoom, Meet). Click Start, select the tab/screen and check 'Share audio'."
                : "Combines microphone + system audio. Useful for translating and speaking simultaneously."}
            </p>
          )}
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            onClick={startRecording}
            disabled={isRecording}
            className="btn-primary flex items-center gap-2"
            data-testid="start-button"
            aria-label={t("start")}
          >
            <Play className="w-5 h-5" />
            {t("start")}
          </Button>
          <Button
            onClick={stopRecording}
            disabled={!isRecording}
            className="btn-destructive flex items-center gap-2"
            data-testid="stop-button"
            aria-label={t("stop")}
          >
            <Square className="w-5 h-5" />
            {t("stop")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
